import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

import { InputPassword } from '../InputPassword';
import { styledGeneralErrorStyle } from '../shared/shared.styles.css';
import { useEnforcedChangePassword } from '../../hooks/useEnforcedChangePassword';
import { WithTrackingData } from '../../AuthModal.types';
import { UserWithNextBestAction } from '../../hooks/authMutationHook.types';

type ChangePasswordFormData = {
    new_password: string;
    confirm_password: string;
};

type ChangePasswordFormProps = {
    email: string;
    oldPassword: string;
    onCompleted: (user: UserWithNextBestAction) => void;
} & WithTrackingData;

const PASSWORD_MIN_LENGTH = 8;
const detailsPasswordFieldGuidance =
    'Password must be at least 8 characters containing 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character';
const minimumLengthGuidance = 'Minimum 8 characters';
const verifyPasswordGuidance = 'Please enter the same value again';

export const ChangePasswordForm = ({
    email,
    oldPassword,
    onCompleted,
    trackingData
}: ChangePasswordFormProps) => {
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm<ChangePasswordFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const password = useRef({});
    password.current = watch('new_password', '');

    const changePasswordAndAuthenticate = useEnforcedChangePassword({
        trackingData
    });

    const onSubmit = handleSubmit(async (formData) => {
        setIsLoading(true);

        try {
            const user = await changePasswordAndAuthenticate({
                email,
                password: oldPassword,
                newPassword: formData.new_password,
                newPasswordConfirm: formData.confirm_password
            });

            onCompleted(user);
        } catch (e) {
            setIsError(true);
            setIsLoading(false);
        }
    });

    return (
        <form onSubmit={onSubmit} noValidate method="post">
            <Box pb="S400">
                <InputPassword
                    autoComplete="new-password"
                    label="New password"
                    aria-required
                    helpText={detailsPasswordFieldGuidance}
                    errorText={
                        errors.new_password &&
                        (errors.new_password.message ||
                            detailsPasswordFieldGuidance)
                    }
                    {...register('new_password', {
                        required: true,
                        minLength: {
                            value: PASSWORD_MIN_LENGTH,
                            message: minimumLengthGuidance
                        },
                        validate: {
                            // Change those to specific message when we have the copy in V2
                            hasSpecialChar: (v: string) =>
                                /[\W_]/.test(v) || detailsPasswordFieldGuidance,
                            hasNumber: (v: string) =>
                                /[0-9]/.test(v) || detailsPasswordFieldGuidance,
                            hasUppercase: (v: string) =>
                                /[A-Z]/.test(v) || detailsPasswordFieldGuidance,
                            hasLowercase: (v: string) =>
                                /[a-z]/.test(v) || detailsPasswordFieldGuidance
                        }
                    })}
                />
            </Box>

            <InputPassword
                autoComplete="new-password"
                label="Verify new password"
                aria-required
                errorText={
                    errors.confirm_password &&
                    (errors.confirm_password.message || verifyPasswordGuidance)
                }
                {...register('confirm_password', {
                    required: true,
                    validate: (value) =>
                        value === password.current || verifyPasswordGuidance
                })}
            />

            <Box pt="S500" display="flex" flexWrap="wrap">
                <Box pb="S400">
                    <div role="alert">
                        {isError && (
                            <p
                                className={`${styledGeneralErrorStyle} ${fontGroups.fork}`}
                                data-testid="general-error"
                            >
                                Sorry, something went wrong. Please try again.
                            </p>
                        )}
                    </div>
                </Box>

                <Button
                    category="primary"
                    loading={isLoading && !isError}
                    loadingText="Submitting"
                    fw
                >
                    Submit
                </Button>
            </Box>
        </form>
    );
};
